import { useEffect, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, errMsg, inr, fmtDate } from "../lib/api";
import { exportDayExcel } from "../lib/excel";
import { WhatsAppShareButton } from "./WhatsAppShareButton";
import { useAuth } from "../context/AuthContext";

export const DayDetailDialog = ({ date, onClose }) => {
  const [day, setDay] = useState(null);
  const { can } = useAuth();
  useEffect(() => {
    setDay(null);
    if (date) api.get("/day-entries", { params: { date } }).then((r) => setDay(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [date]);

  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="rounded-none sm:max-w-3xl bg-surface border-line max-h-[85vh] overflow-y-auto" data-testid="day-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Entries on {date && fmtDate(date)}</DialogTitle>
        </DialogHeader>
        {!day ? <p className="text-sm text-muted-c py-6">Loading…</p> : (
          <>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="border border-line p-3"><p className="text-xs text-muted-c">Deposit</p><p className="font-mono text-pos" data-testid="day-total-deposit">{inr(day.total_deposit)}</p></div>
              <div className="border border-line p-3"><p className="text-xs text-muted-c">Withdraw</p><p className="font-mono text-neg" data-testid="day-total-withdraw">{inr(day.total_withdraw)}</p></div>
              <div className="border border-line p-3"><p className="text-xs text-muted-c">Net</p><p className="font-mono text-net" data-testid="day-total-net">{inr(day.balance)}</p></div>
            </div>
            <div className="overflow-x-auto border border-line">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-head"><tr className="font-mono text-[11px] uppercase tracking-wider text-muted-c text-left">
                  <th className="px-3 py-2">Book</th><th className="px-3 py-2">Branch</th><th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Mode</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Withdraw</th>
                </tr></thead>
                <tbody>
                  {day.entries.map((e) => (
                    <tr key={e.id} className="border-t border-line/60" data-testid={`day-entry-row-${e.id}`}>
                      <td className="px-3 py-2 font-medium">{e.site_name}</td><td className="px-3 py-2">{e.branch_name}</td>
                      <td className="px-3 py-2 text-ink2">{e.description || "-"}</td><td className="px-3 py-2">{e.mode}</td>
                      <td className="px-3 py-2 text-right font-mono text-pos">{e.type === "deposit" ? inr(e.amount) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono text-neg">{e.type === "withdraw" ? inr(e.amount) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2">
              {can("export") && <Button variant="outline" onClick={() => exportDayExcel(day)} disabled={!day.entries.length} data-testid="day-export-excel-button"
                className="rounded-none border-brand text-brand bg-transparent hover:bg-brand/10 w-fit">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
              </Button>}
              {can("share") && <WhatsAppShareButton day={day} testid="day-whatsapp-share-button" />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
